import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Globe, User, Lock } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const { login } = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const apiBaseUrl = import.meta.env.VITE_API_URL || '';

  // Handle Google OAuth callback params
  useEffect(() => {
    const token = searchParams.get('token');
    const userData = searchParams.get('user');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(errorParam);
      setSearchParams({}, { replace: true });
      return;
    }

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        login(token, user);
        navigate('/', { replace: true });
      } catch {
        setError('Failed to process Google sign-in');
      }
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, login, navigate, setSearchParams]);

  useEffect(() => {
    fetch(`${apiBaseUrl}/api/auth/setup`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setNeedsSetup(data.data.needs_setup);
          setGoogleEnabled(data.data.google_enabled || false);
        }
      })
      .catch(() => {});
  }, [apiBaseUrl]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = needsSetup ? '/api/auth/setup' : '/api/auth/login';
      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success) {
        login(data.data.token, data.data.user);
        navigate('/');
      } else {
        setError(data.error || 'Login failed');
      }
    } catch {
      setError('Failed to connect to API');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    window.location.href = `${apiBaseUrl}/api/auth/google`;
  };

  if (needsSetup === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-900 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-brand-600 flex items-center justify-center mx-auto shadow-lg shadow-brand-600/30">
            <Globe className="h-7 w-7 text-white" />
          </div>
          <h1 className="mt-5 text-2xl font-bold text-white">
            DomainHQ
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            {needsSetup
              ? 'Create your admin account to get started'
              : 'Sign in to access the dashboard'}
          </p>
        </div>

        <div className="bg-surface rounded-2xl shadow-xl p-6 space-y-4 dark:ring-1 dark:ring-white/10">
          {/* Google Sign-In button */}
          {googleEnabled && !needsSetup && (
            <>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="flex items-center justify-center gap-3 w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-surface hover:bg-slate-50 transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Sign in with Google
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-surface px-2 text-slate-400">or sign in with password</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="label">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="input pl-10"
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={needsSetup ? 'Choose a password (min 8 chars)' : 'Enter your password'}
                  className="input pl-10"
                  autoComplete={needsSetup ? 'new-password' : 'current-password'}
                  required
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading
                ? (needsSetup ? 'Creating Account...' : 'Signing In...')
                : (needsSetup ? 'Create Account' : 'Sign In')}
            </button>
          </form>
        </div>

        {needsSetup && (
          <p className="mt-4 text-center text-sm text-slate-500">
            This will create the first admin account.
          </p>
        )}
      </div>
    </div>
  );
}
